import { app } from "../config/firebase";

export const CONSENT_KEY = "fd_cookie_consent";
export const CONSENT_VERSION = 1;
export const OPEN_SETTINGS_EVENT = "fd:open-cookie-settings";

let analytics = null;
let loading = null;

/** Stored decision, or null when the user has not decided (or the policy version changed). */
export function getConsent() {
  try {
    const raw = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
    return raw && raw.version === CONSENT_VERSION ? raw : null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return getConsent()?.analytics === true;
}

export async function setConsent({ analytics: allowAnalytics }) {
  const value = { version: CONSENT_VERSION, necessary: true, analytics: !!allowAnalytics, decidedAt: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* private mode: the decision only lasts for this page */
  }
  if (value.analytics) await initAnalytics();
  else await disableAnalytics();
  return value;
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

/** Loads Firebase Analytics only after explicit consent (KVKK / GDPR). */
export async function initAnalytics() {
  if (!hasAnalyticsConsent() || process.env.REACT_APP_ANALYTICS_DISABLED === "true") return null;
  if (analytics) return analytics;
  if (!loading) {
    loading = (async () => {
      const mod = await import("firebase/analytics");
      if (!(await mod.isSupported())) return null;
      const instance = mod.getAnalytics(app);
      mod.setAnalyticsCollectionEnabled(instance, true);
      analytics = { mod, instance };
      return analytics;
    })().catch(() => null).finally(() => {
      loading = null;
    });
  }
  return loading;
}

function clearAnalyticsCookies() {
  if (typeof document === "undefined") return;
  document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter((name) => /^_ga/.test(name)).forEach((name) => {
    const host = window.location.hostname;
    const domains = ["", host, `.${host.split(".").slice(-2).join(".")}`];
    domains.forEach((d) => {
      document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
}

export async function disableAnalytics() {
  if (analytics) analytics.mod.setAnalyticsCollectionEnabled(analytics.instance, false);
  clearAnalyticsCookies();
}

export function trackEvent(name, params = {}) {
  if (!analytics || !hasAnalyticsConsent()) return;
  analytics.mod.logEvent(analytics.instance, name, params);
}

export function trackPageView(path) {
  trackEvent("page_view", { page_path: path, page_location: window.location.href });
}

export function __resetAnalyticsForTests() {
  analytics = null;
  loading = null;
}
